import React, { ElementType, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon, ViewIcon } from '@chakra-ui/icons';
import { ArrowUpRight, CalendarDays, FileText, Plus, User2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteOrgQuote, getOrgQuotes } from '../hooks/org_quotes';
import { getCompanyName, Quote } from '../utils/types';
import {
  QuoteCommandBar,
  QuoteDashboardSkeleton,
  QuoteEmptyState,
  QuoteHero,
  QuotePrimaryButton,
  QuoteShell,
  QuoteStatusBadge,
  formatQuoteDate,
  formatQuoteMoney,
  quoteBorder,
  quoteSurface,
} from './quotes/components/quoteUi';

const normalizeQuotes = (data: unknown): Quote[] => {
  if (Array.isArray(data)) {
    return data as Quote[];
  }

  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: Quote[] }).results;
  }

  return [];
};

const getQuoteRecipient = (quote: Quote) =>
  `${quote.lead?.title ? `${quote.lead.title} ` : ''}${quote.lead?.first_name || ''} ${quote.lead?.second_name || ''}`.trim() ||
  'Unnamed customer';

const sortQuotes = (quotes: Quote[], sort: string) => {
  const next = [...quotes];

  switch (sort) {
    case 'amount-asc':
      return next.sort((a, b) => a.total_amount - b.total_amount);
    case 'amount-desc':
      return next.sort((a, b) => b.total_amount - a.total_amount);
    case 'created-asc':
      return next.sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
    case 'created-desc':
    default:
      return next.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
  }
};

type QuoteItemProps = {
  quote: Quote;
  onOpen: (quoteId: string) => void;
  onDelete: (quote: Quote) => void;
};

const QuoteTableRow = ({ quote, onDelete, onOpen }: QuoteItemProps) => (
  <Tr _hover={{ bg: 'gray.50' }} transition="background 0.16s ease">
    <Td py={4}>
      <VStack align="flex-start" spacing={1}>
        <Text fontWeight="800" color="gray.900" cursor="pointer" onClick={() => onOpen(quote.id)}>
          {quote.reference_number}
        </Text>
        <Text fontSize="sm" color="gray.500">
          {getQuoteRecipient(quote)}
        </Text>
      </VStack>
    </Td>
    <Td py={4}>
      <VStack align="flex-start" spacing={1}>
        <Text color="gray.900" fontWeight="600">
          {getCompanyName(quote.lead?.company) || 'No company'}
        </Text>
        <Text fontSize="sm" color="gray.500">
          {quote.lead?.email || quote.lead?.phone || 'No contact method'}
        </Text>
      </VStack>
    </Td>
    <Td py={4}>
      <VStack align="flex-start" spacing={1}>
        <Text color="gray.900" fontWeight="700">
          {formatQuoteMoney(quote.total_amount)}
        </Text>
        <Text fontSize="sm" color="gray.500">
          {quote.items?.length || 0} item{(quote.items?.length || 0) === 1 ? '' : 's'}
        </Text>
      </VStack>
    </Td>
    <Td py={4}>
      <VStack align="flex-start" spacing={1}>
        <Text color="gray.700">{formatQuoteDate(quote.created_at)}</Text>
        <Text fontSize="sm" color="gray.500">
          {quote.valid_until ? `Valid until ${formatQuoteDate(quote.valid_until)}` : 'No expiry set'}
        </Text>
      </VStack>
    </Td>
    <Td py={4}>
      <QuoteStatusBadge status={quote.status} />
    </Td>
    <Td py={4}>
      <HStack justify="flex-end" spacing={1}>
        <Tooltip label="Open quote" hasArrow>
          <IconButton
            aria-label="Open quote"
            icon={<ViewIcon />}
            variant="ghost"
            onClick={() => onOpen(quote.id)}
          />
        </Tooltip>
        <Tooltip label="Delete quote" hasArrow>
          <IconButton
            aria-label="Delete quote"
            icon={<DeleteIcon />}
            colorScheme="red"
            variant="ghost"
            onClick={() => onDelete(quote)}
          />
        </Tooltip>
      </HStack>
    </Td>
  </Tr>
);

const QuoteCard = ({ quote, onDelete, onOpen }: QuoteItemProps) => (
  <Box
    bg={quoteSurface}
    borderWidth="1px"
    borderColor={quoteBorder}
    borderRadius="24px"
    overflow="hidden"
    boxShadow="0 14px 36px rgba(15, 23, 42, 0.05)"
  >
    <Stack spacing={4} p={4}>
      <HStack justify="space-between" align="flex-start">
        <VStack align="flex-start" spacing={1}>
          <Text fontWeight="800" fontSize="lg" color="gray.900">
            {quote.reference_number}
          </Text>
          <Text fontSize="sm" color="gray.500">
            {getQuoteRecipient(quote)}
          </Text>
        </VStack>
        <QuoteStatusBadge status={quote.status} />
      </HStack>

      <Stack spacing={3}>
        <HStack spacing={2} color="gray.600">
          <Box as={User2 as ElementType} boxSize={4} />
          <Text fontSize="sm">{getCompanyName(quote.lead?.company) || 'No company linked'}</Text>
        </HStack>
        <HStack spacing={2} color="gray.600">
          <Box as={CalendarDays as ElementType} boxSize={4} />
          <Text fontSize="sm">{formatQuoteDate(quote.created_at)}</Text>
        </HStack>
        <HStack spacing={2} color="gray.600">
          <Box as={FileText as ElementType} boxSize={4} />
          <Text fontSize="sm">
            {quote.items?.length || 0} item{(quote.items?.length || 0) === 1 ? '' : 's'}
          </Text>
        </HStack>
      </Stack>

      <HStack justify="space-between">
        <VStack align="flex-start" spacing={0}>
          <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.08em">
            Total
          </Text>
          <Text fontWeight="800" fontSize="xl" color="gray.900">
            {formatQuoteMoney(quote.total_amount)}
          </Text>
        </VStack>
        <HStack spacing={2}>
          <Button
            borderRadius="16px"
            rightIcon={<Box as={ArrowUpRight as ElementType} />}
            onClick={() => onOpen(quote.id)}
          >
            Open
          </Button>
          <IconButton
            aria-label="Delete quote"
            icon={<DeleteIcon />}
            borderRadius="16px"
            variant="ghost"
            colorScheme="red"
            onClick={() => onDelete(quote)}
          />
        </HStack>
      </HStack>
    </Stack>
  </Box>
);

export default function QuotesPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('created-desc');
  const [view, setView] = useState<'table' | 'cards'>('table');

  const fetchQuotes = async () => {
    if (!orgId) {
      return;
    }

    setLoading(true);
    const response = await getOrgQuotes(orgId);
    setLoading(false);

    if (!response.success) {
      toast({
        title: 'Could not load quotations',
        description: 'Please refresh and try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    setQuotes(normalizeQuotes(response.data));
  };

  useEffect(() => {
    fetchQuotes();
  }, [orgId]);

  const filteredQuotes = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = quotes.filter((quote) => {
      const matchesSearch =
        !query ||
        quote.reference_number?.toLowerCase().includes(query) ||
        getQuoteRecipient(quote).toLowerCase().includes(query) ||
        getCompanyName(quote.lead?.company).toLowerCase().includes(query) ||
        String(quote.total_amount).includes(query);

      const matchesStatus = status === 'all' || quote.status === status;
      return matchesSearch && matchesStatus;
    });

    return sortQuotes(filtered, sort);
  }, [quotes, search, status, sort]);

  const stats = useMemo(
    () => [
      { label: 'Quotes', value: String(quotes.length) },
      { label: 'Drafts', value: String(quotes.filter((quote) => quote.status === 'draft').length) },
      { label: 'Sent', value: String(quotes.filter((quote) => quote.status === 'sent').length) },
      {
        label: 'Pipeline value',
        value: formatQuoteMoney(
          quotes.reduce((sum, quote) => sum + Number(quote.total_amount || 0), 0),
        ),
      },
    ],
    [quotes],
  );

  const handleOpen = (quoteId: string) => {
    navigate(`/organization/${orgId}/quotes/view/${quoteId}`);
  };

  const handleDelete = async (quote: Quote) => {
    if (!orgId) {
      return;
    }

    const response = await deleteOrgQuote(orgId, quote.id);
    if (!response.success) {
      toast({
        title: 'Could not delete quotation',
        description: 'Please try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    setQuotes((current) => current.filter((item) => item.id !== quote.id));
    toast({
      title: 'Quotation deleted',
      description: `${quote.reference_number} was removed.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <QuoteShell>
      <QuoteHero
        title="Quotations built for speed, accuracy, and commercial confidence"
        description="Review pipeline-ready quotes, spot drafts that need action, and move from pricing to sending without friction."
        stats={stats}
        primaryAction={
          <QuotePrimaryButton leftIcon={<Plus size={16} />} onClick={() => navigate(`/organization/${orgId}/quotes/new`)}>
            New quote
          </QuotePrimaryButton>
        }
        secondaryAction={
          <Button borderRadius="16px" variant="outline" onClick={fetchQuotes}>
            Refresh
          </Button>
        }
      />

      <QuoteCommandBar
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
        sort={sort}
        onSortChange={setSort}
        view={view}
        onViewChange={setView}
        resultText={`${filteredQuotes.length} of ${quotes.length} quotations shown`}
      />

      {loading ? (
        <QuoteDashboardSkeleton />
      ) : filteredQuotes.length === 0 ? (
        <QuoteEmptyState
          title={quotes.length ? 'No quotations match these filters' : 'No quotations yet'}
          description={
            quotes.length
              ? 'Try widening the filters or searching by a different customer or reference number.'
              : 'Create your first quote to start turning customer interest into structured commercial follow-up.'
          }
          action={
            <QuotePrimaryButton leftIcon={<Plus size={16} />} onClick={() => navigate(`/organization/${orgId}/quotes/new`)}>
              Create quote
            </QuotePrimaryButton>
          }
        />
      ) : view === 'table' ? (
        <TableContainer
          bg={quoteSurface}
          borderWidth="1px"
          borderColor={quoteBorder}
          borderRadius="28px"
          overflow="hidden"
          boxShadow="0 18px 44px rgba(15, 23, 42, 0.05)"
        >
          <Table variant="simple" size="md">
            <Thead bg="gray.50">
              <Tr>
                <Th py={4}>Quote</Th>
                <Th py={4}>Recipient</Th>
                <Th py={4}>Amount</Th>
                <Th py={4}>Issued</Th>
                <Th py={4}>Status</Th>
                <Th py={4} textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredQuotes.map((quote) => (
                <QuoteTableRow key={quote.id} quote={quote} onOpen={handleOpen} onDelete={handleDelete} />
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {filteredQuotes.map((quote) => (
            <QuoteCard key={quote.id} quote={quote} onOpen={handleOpen} onDelete={handleDelete} />
          ))}
        </SimpleGrid>
      )}
    </QuoteShell>
  );
}
